import { useEffect, useState, useRef } from 'react';
import { grantConsent, denyConsent } from '../../../analytics';
import './cookieConsentBanner.css';

const COOKIE_CONSENT_NAME = 'myWebsiteConsentCookie';

function CookieConsentBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const bannerRef = useRef(null);
  const firstFocusableRef = useRef(null);
  const lastFocusableRef = useRef(null);

  useEffect(() => {
    // Check if user has already given consent
    const hasConsent = localStorage.getItem(COOKIE_CONSENT_NAME);
    if (!hasConsent) {
      setShowBanner(true);
    } else if (hasConsent === 'true') {
      grantConsent();
    } else {
      denyConsent();
    }
  }, []);

  // Handle Escape key to close banner
  useEffect(() => {
    if (!showBanner) return;

    const handleKeyDown = (e) => {
      // Close on Escape key
      if (e.key === 'Escape') {
        handleDecline();
      }

      // Focus trap: keep focus within banner
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          // Shift+Tab on first focusable element
          if (document.activeElement === firstFocusableRef.current) {
            e.preventDefault();
            lastFocusableRef.current?.focus();
          }
        } else {
          // Tab on last focusable element
          if (document.activeElement === lastFocusableRef.current) {
            e.preventDefault();
            firstFocusableRef.current?.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    // Set initial focus to first button
    setTimeout(() => {
      firstFocusableRef.current?.focus();
    }, 100);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showBanner]);

  // Listen for external requests to reopen the cookie banner
  useEffect(() => {
    const openHandler = () => {
      setShowBanner(true);
      setTimeout(() => {
        firstFocusableRef.current?.focus();
      }, 100);
    };

    window.addEventListener('open-cookie-banner', openHandler);
    return () => window.removeEventListener('open-cookie-banner', openHandler);
  }, []);

  const handleAccept = () => {
    localStorage.setItem(COOKIE_CONSENT_NAME, 'true');
    setShowBanner(false);
    grantConsent();
  };

  const handleDecline = () => {
    localStorage.setItem(COOKIE_CONSENT_NAME, 'false');
    setShowBanner(false);
    denyConsent();
  };

  if (!showBanner) return null;

  return (
    <div 
      ref={bannerRef}
      className="cookie-consent-banner" 
      role="dialog" 
      aria-modal="true"
      aria-labelledby="cookie-consent-title"
    >
      <div className="cookie-consent-content">
        <div className="cookie-consent-text-wrapper">
          <p id="cookie-consent-title" className="cookie-consent-text">
            We use optional analytics cookies to improve the website. Read our &nbsp;
            <a href="/privacy" className="cookie-consent-link">
              Privacy Policy
            </a>
          </p>
        </div>
        <div className="cookie-consent-buttons">
          <button
            ref={firstFocusableRef}
            className="cookie-consent-btn accept"
            onClick={handleAccept}
            aria-label="Accept all cookies"
          >
            Accept All
          </button>
          <button
            ref={lastFocusableRef}
            className="cookie-consent-btn decline"
            onClick={handleDecline}
            aria-label="Decline cookies"
          >
            Decline
          </button>
        </div>
      </div>
    </div>
  );
}

export default CookieConsentBanner;
